import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ServiceCard from "./ServiceCard";
import { industries } from "../../data/industries";
import { states, getCitiesByState } from "../../data/locations";
import { blogPosts } from "../../data/blogPosts";
import { getCategoryName } from "../../data/blogCategories";
import { business } from "../../config/business";

// === KEEP EXACT ORIGINAL IMPORT NAMES (point them to optimized versions in your build) ===
import heroImage from "../assets/hero-industrial.jpg";
import industrialShed from "../assets/industrial-shed.jpg";
import fabrication from "../assets/mezzanine flooring.png";
import industrialStructure from "../assets/2.png";
import prefabBuilding from "../assets/3.png";

import { FaBuilding, FaHardHat, FaRulerCombined, FaClock, FaShieldAlt, FaMoneyBillWave, FaWhatsapp } from "react-icons/fa";
import { ShieldCheck, MapPin, BookOpen, Factory } from "lucide-react";

// Self-hosted fonts – replace Google Fonts blocking
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/700.css";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const Reveal = ({ children, className }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    variants={fadeUp}
    className={className}
  >
    {children}
  </motion.div>
);

const Homepage = () => {
  const services = [
    { title: "Industrial Sheds", description: "Heavy-duty sheds engineered for warehouses, factories, workshops, and industrial production centers.", image: industrialShed, link: "/industrial-sheds" },
    { title: "Fabrication Services", description: "Custom MS/SS steel fabrication, structural frames, trusses, beams, and industrial support systems.", image: fabrication, link: "/FabricationServices" },
    { title: "Industrial Structures", description: "Large-scale industrial frameworks built for machinery, manufacturing, and storage requirements.", image: industrialStructure, link: "/IndustrialStructures" },
    { title: "Prefabricated Buildings", description: "Quick-install, cost-effective prefab units for offices, cabins, godowns, and commercial spaces.", image: prefabBuilding, link: "/pre-fab-structure-buildings" },
  ];

  const features = [
    { icon: <FaBuilding className="text-5xl text-primary mx-auto mb-4" />, title: "High-Quality Materials", description: "Premium MS, SS & GI steel" },
    { icon: <FaHardHat className="text-5xl text-primary mx-auto mb-4" />, title: "Expert Team", description: "Experienced engineers & fabricators" },
    { icon: <FaRulerCombined className="text-5xl text-primary mx-auto mb-4" />, title: "Custom Designs", description: "Tailored to your exact needs" },
    { icon: <FaClock className="text-5xl text-primary mx-auto mb-4" />, title: "Timely Delivery", description: "On-schedule project completion" },
    { icon: <FaShieldAlt className="text-5xl text-primary mx-auto mb-4" />, title: "Heavy-Duty Build", description: "Built to last decades" },
    { icon: <FaMoneyBillWave className="text-5xl text-primary mx-auto mb-4" />, title: "Affordable Solutions", description: "Best value in Hyderabad" },
  ];

  const featuredIndustries = industries.slice(0, 6);
  const featuredPosts = blogPosts.slice(0, 3);
  const tgCities = getCitiesByState("telangana").slice(0, 3);
  const apCities = getCitiesByState("andhra-pradesh").slice(0, 3);

  return (
    <>
      {/* ==== 1. HERO SECTION – FULLY LCP OPTIMIZED (2025 BEST PRACTICES) ==== */}
      <section className="relative h-[100vh] md:h-screen min-h-96 overflow-hidden">
        {/* Preload LCP image – highest priority */}
        <link
          rel="preload"
          fetchPriority="high"
          as="image"
          href={heroImage}
          imageSrcSet={`
            ${heroImage}?format=webp&width=600 600w,
            ${heroImage}?format=webp&width=1200 1200w,
            ${heroImage}?format=webp&width=1920 1920w
          `}
          imageSizes="100vw"
        />

        {/* Modern responsive image with WebP fallback */}
        <picture>
          <source
            srcSet={`
              ${heroImage}?format=webp&width=600 600w,
              ${heroImage}?format=webp&width=1200 1200w,
              ${heroImage}?format=webp&width=1920 1920w
            `}
            sizes="100vw"
            type="image/webp"
          />
          <img
            src={heroImage}
            alt="Vasavi Structures - Leading Industrial Shed & Steel Fabrication Company in Hyderabad"
            className="absolute inset-0 w-full h-full object-cover object-center"
            fetchPriority="high"
            decoding="async"
            loading="eager"
            width="1920"
            height="1080"
            srcSet={`
              ${heroImage}?width=600 600w,
              ${heroImage}?width=1200 1200w,
              ${heroImage}?width=1920 1920w
            `}
            sizes="100vw"
          />
        </picture>

        <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

        <div className="relative z-10 container mx-auto px-6 py-24 text-center text-white flex flex-col justify-center h-full">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-secondary font-bold text-lg mb-4 tracking-wider"
          >
            Welcome to Vasavi Structures
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight"
          >
            Engineered PEB & Industrial<br />
            <span className="text-secondary">Structures Built for Performance.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-lg md:text-xl mb-10 max-w-3xl mx-auto"
          >
            Design, fabrication and construction for warehouses, factories, industrial sheds and
            commercial structures across Telangana and Andhra Pradesh.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center"
          >
            <Link
              to="/enquiry"
              className="bg-secondary text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-secondary/90 transition"
            >
              Get Project Estimate
            </Link>
            <a
              href={business.whatsapp.href}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-[#20bd5a] transition flex items-center justify-center gap-2"
            >
              <FaWhatsapp size={22} aria-hidden="true" /> WhatsApp Us
            </a>
            <a
              href={business.phone.href}
              className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white hover:text-primary transition"
            >
              Call Now
            </a>
          </motion.div>
        </div>
      </section>

      <main>
        {/* ==== 2. TRUST STRIP ==== */}
        <section className="bg-primary py-8">
          <div className="container mx-auto px-6">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-primary-foreground text-center">
              <span className="font-montserrat font-semibold flex items-center gap-2">
                <ShieldCheck size={20} aria-hidden="true" /> Design & fabrication under one roof
              </span>
              <span className="font-montserrat font-semibold flex items-center gap-2">
                <MapPin size={20} aria-hidden="true" /> Hyderabad-based, statewide delivery
              </span>
              <span className="font-montserrat font-semibold flex items-center gap-2">
                <Factory size={20} aria-hidden="true" /> Site-specific structural engineering
              </span>
            </div>
          </div>
        </section>

        {/* ==== 3. SERVICES ==== */}
        <Reveal className="py-20 bg-gray-50">
          <section>
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Core Services</h2>
              <p className="text-xl text-gray-600 mb-16">
                From concept to completion — we deliver industrial infrastructure that powers your business.
              </p>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10"
              >
                {services.map((service, i) => (
                  <motion.div key={i} variants={fadeUp}>
                    <ServiceCard {...service} index={i} />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        </Reveal>

        {/* ==== 4. INDUSTRIES PREVIEW ==== */}
        <Reveal className="py-20">
          <section>
            <div className="container mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Built for <span className="text-secondary">Your Industry</span>
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Every industry has different structural priorities — we design around yours.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {featuredIndustries.map((ind) => (
                  <Link
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    className="p-6 bg-card rounded-xl shadow-md card-hover border border-border text-center"
                  >
                    <Factory className="text-primary mx-auto mb-3" size={26} aria-hidden="true" />
                    <h3 className="font-montserrat font-bold text-foreground">{ind.name}</h3>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-10">
                <Link to="/industries" className="btn-outline inline-block px-8 py-3">
                  View All Industries
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== 5. LOCATIONS PREVIEW ==== */}
        <Reveal className="py-20 bg-gray-50">
          <section>
            <div className="container mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Serving <span className="text-secondary">Telangana & Andhra Pradesh</span>
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Design and fabrication at our Hyderabad facility, delivered and erected across both states.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {states.map((state) => {
                  const cities = state.slug === "telangana" ? tgCities : apCities;
                  return (
                    <div key={state.slug} className="p-6 bg-card rounded-xl shadow-md border border-border">
                      <h3 className="font-montserrat font-bold text-xl text-foreground mb-4 flex items-center gap-2">
                        <MapPin className="text-secondary" size={20} aria-hidden="true" /> {state.name}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {cities.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/locations/${state.slug}/${c.slug}`}
                            className="px-3 py-1 bg-muted rounded-full text-sm text-foreground hover:bg-primary hover:text-primary-foreground transition"
                          >
                            {c.name}
                          </Link>
                        ))}
                      </div>
                      <Link to={`/locations/${state.slug}`} className="text-primary font-semibold text-sm hover:underline">
                        View all {state.name} locations →
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== 6. WHY CHOOSE US ==== */}
        <Reveal className="py-20">
          <section>
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Why Choose <span className="text-primary">Vasavi</span> <span className="text-secondary">Structures?</span>
              </h2>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mt-16"
              >
                {features.map((f) => (
                  <motion.div key={f.title} variants={fadeUp} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition">
                    {f.icon}
                    <h3 className="font-bold text-lg mt-4">{f.title}</h3>
                    <p className="text-gray-600 text-sm mt-2">{f.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        </Reveal>

        {/* ==== 7. RESOURCES PREVIEW ==== */}
        <Reveal className="py-20 bg-gray-50">
          <section>
            <div className="container mx-auto px-6">
              <div className="text-center mb-14">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Guides & <span className="text-secondary">Resources</span>
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Practical, in-depth guides on PEB construction, cost planning and engineering.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/resources/${post.slug}`}
                    className="p-6 bg-card rounded-xl shadow-md card-hover border border-border flex flex-col"
                  >
                    <p className="text-xs text-secondary font-semibold mb-2 flex items-center gap-1">
                      <BookOpen size={12} aria-hidden="true" /> {getCategoryName(post.category)}
                    </p>
                    <h3 className="font-montserrat font-bold text-foreground">{post.title}</h3>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-10">
                <Link to="/resources" className="btn-outline inline-block px-8 py-3">
                  View All Resources
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== 8. FINAL CTA ==== */}
        <section className="py-20 bg-primary text-white text-center">
          <div className="container mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Start Your Project With Confidence</h2>
            <p className="text-xl mb-10 max-w-3xl mx-auto">
              Partner with Vasavi Structures for all your industrial building and fabrication needs — where quality meets reliability.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/enquiry"
                className="bg-secondary text-white px-12 py-6 rounded-lg font-bold text-xl hover:bg-secondary/90 transition"
              >
                Send Enquiry Now
              </Link>
              <a
                href={business.whatsapp.href}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-12 py-6 rounded-lg font-bold text-xl hover:bg-[#20bd5a] transition flex items-center justify-center gap-2"
              >
                <FaWhatsapp size={24} aria-hidden="true" /> WhatsApp Us
              </a>
              <Link
                to="/contact"
                className="bg-white text-primary px-12 py-6 rounded-lg font-bold text-xl hover:bg-gray-100 transition"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Homepage;
